import React from "react";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
} from "lucide-react";
import { HomeHero } from "../marketing/HomeHero";
import { HomeBottomCta } from "../marketing/HomeBottomCta";
import {
  FeatureShowcase,
  HOMEPAGE_SHOWCASES,
} from "../marketing/FeatureShowcase";
import {
  HOMEPAGE_META,
  HOMEPAGE_FAQS,
  OG_IMAGE,
  SITE_URL,
  buildFaqSchema,
  buildHomepageSchema,
} from "../../content/homepageSeo";
import { SeelayBuiltByAnswer } from "../common/SeelayLogoLink";
import { PersonaBento } from "../marketing/PersonaBento";

function AboutPage() {
  const homepageSchema = buildHomepageSchema();
  const faqSchema = buildFaqSchema(HOMEPAGE_FAQS);

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>{HOMEPAGE_META.title}</title>
        <meta name="description" content={HOMEPAGE_META.description} />
        <meta name="keywords" content={HOMEPAGE_META.keywords} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={`${SITE_URL}/`} />
        <link rel="home" href={`${SITE_URL}/`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta property="og:site_name" content="PageTest" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={HOMEPAGE_META.title} />
        <meta property="og:description" content={HOMEPAGE_META.description} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={HOMEPAGE_META.title} />
        <meta name="twitter:description" content={HOMEPAGE_META.description} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">
          {JSON.stringify(homepageSchema)}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <main id="main-content" className="mx-auto max-w-6xl px-4">
        <HomeHero />

        {/* Platform showcases */}
        <section>
          {HOMEPAGE_SHOWCASES.map((showcase) => (
            <FeatureShowcase key={showcase.headline} {...showcase} />
          ))}
        </section>

        <PersonaBento />

        {/* FAQ – visible content matches FAQPage schema for SEO / GEO */}
        <section
          id="faq"
          className="py-16 sm:py-20"
          aria-labelledby="faq-heading"
        >
          <h2
            id="faq-heading"
            className="text-2xl sm:text-3xl font-[450] text-foreground tracking-[-0.06em] max-w-xl"
          >
            Frequently asked questions
          </h2>
          <div className="mt-10 divide-y divide-border border border-border rounded-xl overflow-hidden">
            {HOMEPAGE_FAQS.map(({ question, answer }) => (
              <details key={question} className="group bg-card">
                <summary className="cursor-pointer list-none px-5 sm:px-6 py-5 text-sm font-semibold text-foreground flex items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
                  {question}
                  <ArrowRight
                    size={16}
                    className="shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
                    aria-hidden
                  />
                </summary>
                {question === "Who builds PageTest?" ? (
                  <SeelayBuiltByAnswer />
                ) : (
                  <p className="px-5 sm:px-6 pb-5 text-sm text-muted-foreground">
                    {answer}
                  </p>
                )}
              </details>
            ))}
          </div>
        </section>

        <HomeBottomCta />
      </main>
    </>
  );
}

export default AboutPage;
